/**
 * Pickup scheduler
 * ------------------------------------------------------------------------------
 * Replaces the Zapiet pickup widget. Renders a pickup date/time picker into
 * every [data-pickup-scheduler] container (cart drawer + cart page), persists
 * the selection as the cart attributes `Pickup-Date` (YYYY/MM/DD) and
 * `Pickup-Time` (e.g. "11:00 AM"), and blocks checkout until both are chosen.
 *
 * Products tagged with the configured skip tag (popups, e.g. bagel pre-orders)
 * don't need a pickup slot: when EVERY line in the cart carries the tag the
 * picker hides itself and clears any previously stored attributes.
 *
 * Vanilla JS on purpose: the drawer body is an Alpine v2 x-if template that
 * mounts after the cart state loads, and the cart page swaps .js-contents via
 * the Section Rendering API, so all listeners are delegated to document and
 * rendering is (re)applied whenever an unpopulated container appears.
 */
(function () {
  'use strict';

  var ATTR_DATE = 'Pickup-Date';
  var ATTR_TIME = 'Pickup-Time';

  var config = null;
  var state = {
    cart: null,
    // null = tag lookups still in flight; treat as "required" when validating
    required: null,
    dates: [],
    selectedDate: '',
    selectedTime: ''
  };
  var tagCache = {};
  var refreshTimer = null;

  /* ------------------------------------------------------------------ *
   * Config
   * ------------------------------------------------------------------ */

  function loadConfig() {
    var el = document.querySelector('[data-pickup-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('Pickup scheduler: invalid config JSON', error);
      return null;
    }
  }

  /* ------------------------------------------------------------------ *
   * Date/time helpers — all calculations use the shop timezone
   * ------------------------------------------------------------------ */

  function nowInShopTz() {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: config.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());
    var map = {};
    parts.forEach(function (part) {
      map[part.type] = part.value;
    });
    // Intl may return "24" for midnight
    var hour = map.hour === '24' ? 0 : parseInt(map.hour, 10);
    return {
      date: map.year + '-' + map.month + '-' + map.day,
      minutes: hour * 60 + parseInt(map.minute, 10)
    };
  }

  function addDays(isoDate, days) {
    // Noon UTC keeps the maths clear of any DST edges
    var date = new Date(isoDate + 'T12:00:00Z');
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  function weekdayOf(isoDate) {
    return new Date(isoDate + 'T12:00:00Z').getUTCDay(); // 0 = Sunday
  }

  function displayDate(isoDate) {
    return new Date(isoDate + 'T12:00:00Z').toLocaleDateString(config.dateLocale || 'en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  function attributeDate(isoDate) {
    return isoDate.replace(/-/g, '/');
  }

  function isoFromAttribute(value) {
    return (value || '').replace(/\//g, '-');
  }

  function timeToMinutes(value) {
    var match = /(\d{1,2}):(\d{2})\s*([AP]M?)?/i.exec(value || '');
    if (!match) return null;
    var hours = parseInt(match[1], 10);
    var meridiem = (match[3] || '').toUpperCase();
    if (meridiem.charAt(0) === 'P' && hours !== 12) hours += 12;
    if (meridiem.charAt(0) === 'A' && hours === 12) hours = 0;
    return hours * 60 + parseInt(match[2], 10);
  }

  function parseBlackoutDates(text) {
    var dates = {};
    (text || '').split(/[\n,]+/).forEach(function (entry) {
      entry = entry.trim();
      if (!entry) return;
      var range = entry.split('..');
      var start = range[0].trim();
      var end = (range[1] || range[0]).trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return;
      var current = start;
      var guard = 0;
      while (current <= end && guard < 366) {
        dates[current] = true;
        current = addDays(current, 1);
        guard++;
      }
    });
    return dates;
  }

  function slotsForDate(isoDate, now) {
    var raw = config.times[weekdayOf(isoDate)] || '';
    var slots = raw.split(',').map(function (slot) {
      return slot.trim();
    }).filter(Boolean);
    if (isoDate === now.date) {
      slots = slots.filter(function (slot) {
        var minutes = timeToMinutes(slot);
        return minutes === null || minutes > now.minutes;
      });
    }
    return slots;
  }

  function computeDates() {
    var now = nowInShopTz();
    var cutoff = timeToMinutes(config.cutoffTime);
    var minOffset = cutoff === null || now.minutes < cutoff
      ? config.prepDays
      : config.prepDaysAfterCutoff;
    var blackouts = parseBlackoutDates(config.blackoutDates);
    var dates = [];
    for (var offset = minOffset; offset <= config.maxDays; offset++) {
      var isoDate = addDays(now.date, offset);
      if (blackouts[isoDate]) continue;
      var slots = slotsForDate(isoDate, now);
      if (!slots.length) continue;
      dates.push({ iso: isoDate, label: displayDate(isoDate), slots: slots });
    }
    return dates;
  }

  /* ------------------------------------------------------------------ *
   * Cart + product tag lookups
   * ------------------------------------------------------------------ */

  function fetchCart() {
    return fetch('/cart.js').then(function (response) {
      return response.json();
    });
  }

  function updateAttributes(date, time) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: (function () {
          var attributes = {};
          attributes[ATTR_DATE] = date;
          attributes[ATTR_TIME] = time;
          return attributes;
        })()
      })
    }).then(function (response) {
      return response.json();
    });
  }

  function handleHasSkipTag(handle) {
    if (!(handle in tagCache)) {
      tagCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (product) {
          var skipTag = (config.skipTag || '').toLowerCase();
          if (!skipTag) return false;
          return (product.tags || []).some(function (tag) {
            return tag.toLowerCase() === skipTag;
          });
        })
        .catch(function () {
          // If the lookup fails, require a pickup slot rather than skip it
          return false;
        });
    }
    return tagCache[handle];
  }

  function pickerRequired(cart) {
    if (!cart.item_count) return Promise.resolve(false);
    var lookups = cart.items.map(function (item) {
      return handleHasSkipTag(item.handle);
    });
    return Promise.all(lookups).then(function (results) {
      return results.some(function (hasTag) {
        return !hasTag;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  function instances() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-pickup-scheduler]'));
  }

  function findDate(isoDate) {
    for (var i = 0; i < state.dates.length; i++) {
      if (state.dates[i].iso === isoDate) return state.dates[i];
    }
    return null;
  }

  function buildOptions(select, placeholder, values, selected) {
    select.innerHTML = '';
    var placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);
    values.forEach(function (value) {
      var option = document.createElement('option');
      option.value = value.value;
      option.textContent = value.label;
      option.selected = value.value === selected;
      select.appendChild(option);
    });
    select.value = selected || '';
  }

  function render() {
    instances().forEach(function (container) {
      container.classList.toggle('hidden', state.required !== true);
      container.setAttribute('data-pickup-rendered', 'true');
      if (state.required !== true) return;

      var dateSelect = container.querySelector('[data-pickup-date]');
      var timeSelect = container.querySelector('[data-pickup-time]');
      if (!dateSelect || !timeSelect) return;

      buildOptions(dateSelect, config.strings.selectDate, state.dates.map(function (date) {
        return { value: date.iso, label: date.label };
      }), state.selectedDate);

      var selectedDate = findDate(state.selectedDate);
      var slots = selectedDate ? selectedDate.slots : [];
      timeSelect.disabled = !selectedDate;
      buildOptions(timeSelect, config.strings.selectTime, slots.map(function (slot) {
        return { value: slot, label: slot };
      }), state.selectedTime);

      if (state.selectedDate && state.selectedTime) hideError(container);
    });
  }

  function showError(message) {
    instances().forEach(function (container) {
      var errorEl = container.querySelector('[data-pickup-error]');
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    });
    var visible = instances().filter(function (container) {
      return container.offsetParent !== null;
    })[0];
    if (visible) visible.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function hideError(container) {
    var errorEl = container.querySelector('[data-pickup-error]');
    if (errorEl) errorEl.classList.add('hidden');
  }

  function hideErrors() {
    instances().forEach(hideError);
  }

  /* ------------------------------------------------------------------ *
   * State refresh
   * ------------------------------------------------------------------ */

  function refresh() {
    fetchCart().then(function (cart) {
      state.cart = cart;
      return pickerRequired(cart).then(function (required) {
        state.required = required;
        var storedDate = isoFromAttribute(cart.attributes[ATTR_DATE]);
        var storedTime = cart.attributes[ATTR_TIME] || '';

        if (!required) {
          // Popup-only (or empty) cart: stale pickup attributes must not
          // end up on the order
          if (cart.item_count && (storedDate || storedTime)) {
            updateAttributes('', '');
          }
          render();
          return;
        }

        state.dates = computeDates();
        var validDate = findDate(storedDate);
        if (validDate && validDate.slots.indexOf(storedTime) !== -1) {
          state.selectedDate = storedDate;
          state.selectedTime = storedTime;
        } else {
          state.selectedDate = '';
          state.selectedTime = '';
          // Selection went stale (past cutoff, new blackout, …) — clear it
          if (storedDate || storedTime) updateAttributes('', '');
        }
        render();
      });
    }).catch(function (error) {
      console.error('Pickup scheduler: could not refresh cart', error);
    });
  }

  function debouncedRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 150);
  }

  /* ------------------------------------------------------------------ *
   * Events
   * ------------------------------------------------------------------ */

  function onDateChange(select) {
    state.selectedDate = select.value;
    state.selectedTime = '';
    render();
  }

  function onTimeChange(select) {
    state.selectedTime = select.value;
    render();
    if (state.selectedDate && state.selectedTime) {
      updateAttributes(attributeDate(state.selectedDate), state.selectedTime);
    }
  }

  function validateCheckout(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart') === -1) return;
    // Only guard actual checkout submits, not the no-JS "Update" button
    if (event.submitter && event.submitter.name && event.submitter.name !== 'checkout') return;
    if (state.required === false) return;

    // Re-validate against a fresh window: the tab may have sat open past the
    // cutoff time or into a blackout date
    if (state.required === true && state.selectedDate && state.selectedTime) {
      state.dates = computeDates();
      var date = findDate(state.selectedDate);
      if (date && date.slots.indexOf(state.selectedTime) !== -1) return;
      state.selectedDate = '';
      state.selectedTime = '';
      updateAttributes('', '');
      render();
    }

    event.preventDefault();
    event.stopPropagation();
    showError(config.strings.errorRequired);
  }

  function init() {
    config = loadConfig();
    if (!config) return;

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (target.matches('[data-pickup-date]')) onDateChange(target);
      if (target.matches('[data-pickup-time]')) onTimeChange(target);
    });

    document.addEventListener('submit', validateCheckout, true);

    ['baseline:modalcart:itemcountupdate', 'baseline:modalcart:afteradditem'].forEach(function (name) {
      document.body.addEventListener(name, debouncedRefresh);
    });

    // The drawer body is an Alpine x-if template: containers (re)enter the
    // DOM after our initial render, so re-render whenever a fresh one appears
    new MutationObserver(function () {
      var needsRender = instances().some(function (container) {
        return !container.hasAttribute('data-pickup-rendered');
      });
      if (needsRender) render();
    }).observe(document.body, { childList: true, subtree: true });

    refresh();

    // Debug/QA handle
    window.pickupScheduler = {
      state: state,
      config: config,
      refresh: refresh,
      computeDates: computeDates
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
